import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  ALBUM_MEDIA_TYPE,
  INFLUENCER_REQUEST_ONBOARDING_TYPE,
  MEDIA_ASSET_SALE_TAG,
  MIN_NUM_OF_FREE_VIDEOS,
  MIN_NUM_OF_PREMIUM_VIDEOS,
  UPLOAD_FILE_CONSTRAINTS,
} from '../../utils/constants';
import { selectUser, updateNumOfUpdatedMedia } from '../../store/slices/user/slice';
import { UPLOAD_FREE_VIDEOS, UPLOAD_MEDIA, UPLOAD_PAID_VIDEOS } from '../App/routes';
import { notifyError, notifyInfo } from '../../utils/notify';
import {
  deleteInfluencerRequestAlbumMedia,
  fetchInfluencerRequestAlbumMedia,
  uploadAssistedVideo,
} from '../../store/slices/influencerRequest/asyncThunks';
import UnsavedChangesModal from '../modals/UnsavedChangesModal/UnsavedChangesModal';
import UploadMediaButton from '../UploadMediaButton/UploadMediaButton';
import {
  BackIconWrapper,
  Container,
  Content,
  EmptyDiv,
  Header,
  HeaderContainer,
  HeaderRow,
  HeaderText,
  Line,
  SkeletonContainer,
  StyledButton,
} from './UploadAssistedVideos.styles';
import MediaItem from '../MediaItem/MediaItem';
import { ReactComponent as BackIcon } from '../../assets/icons/chevron_left.svg';
import { Text18Bold, Text20Bold } from '../utils/Text/Text.styles';
import {
  selectFetchInfluencerRequestAlbumMediaInfo,
  selectInfluencerRequestActionsPending,
} from '../../store/slices/influencerRequest/slice';
import { useConfirmationModal } from '../modals/ConfirmationModal/ConfirmationModal';
import PreviewAlbumMediaModal from '../modals/PreviewAlbumMediaModal/PreviewAlbumMediaModal';

const UploadAssistedVideos = ({ paid }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUser);
  const { deleteInfluencerRequestAlbumMedia: deleteInfluencerRequestAlbumMediaPending } = useSelector(
    selectInfluencerRequestActionsPending,
  );
  const previewAlbumMediaModalRef = useRef();
  const [selectedMedia, setSelectedMedia] = useState(null);
  const { data, pending } = useSelector(selectFetchInfluencerRequestAlbumMediaInfo);
  const [showModal, setShowModal] = useState(false);
  const [videos, setVideos] = useState([]);
  const [uploadingProgress, setUploadingProgress] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const [openModal, closeModal, setConfirmationModalLoader] = useConfirmationModal();

  useEffect(() => {
    if (user.onboardingType === INFLUENCER_REQUEST_ONBOARDING_TYPE.SELF) {
      navigate(paid ? `/${UPLOAD_MEDIA}/${UPLOAD_PAID_VIDEOS}` : `/${UPLOAD_MEDIA}/${UPLOAD_FREE_VIDEOS}`);
    }
  }, [user.onboardingType, navigate, paid]);

  useEffect(() => {
    setConfirmationModalLoader(deleteInfluencerRequestAlbumMediaPending);
  }, [deleteInfluencerRequestAlbumMediaPending, setConfirmationModalLoader]);

  useEffect(() => {
    dispatch(
      fetchInfluencerRequestAlbumMedia({
        kind: ALBUM_MEDIA_TYPE.VIDEO,
        tag: paid ? MEDIA_ASSET_SALE_TAG.SINGLE_PAID : MEDIA_ASSET_SALE_TAG.SINGLE_FREE,
      }),
    );
  }, [dispatch, paid]);

  const myUploadProgress = index => progress => {
    let percentage = progress;
    setUploadingProgress(prev => {
      const newProgress = [...prev];
      newProgress[index] = percentage > 99 ? 99 : percentage.toFixed(1);
      return newProgress;
    });
  };

  useEffect(() => {
    if (uploadingProgress.length && !uploadingProgress.filter(progress => progress !== 100).length) {
      dispatch(
        updateNumOfUpdatedMedia({
          numOfMedia: uploadingProgress.length,
          type: UPLOAD_FILE_CONSTRAINTS.TYPE.VIDEO,
          paid,
        }),
      );
      setLoading(false);
      navigate(`/${UPLOAD_MEDIA}`);
      notifyInfo('Videos uploaded successfully');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadingProgress]);

  const onBack = () => {
    if (videos.length) {
      setShowModal(true);
    } else {
      navigate(`/${UPLOAD_MEDIA}`);
    }
  };

  const onUploadClick = () => {
    setLoading(true);
    for (const [index, video] of videos.entries()) {
      dispatch(
        uploadAssistedVideo({
          video,
          type: paid ? MEDIA_ASSET_SALE_TAG.SINGLE_PAID : MEDIA_ASSET_SALE_TAG.SINGLE_FREE,
          onProgress: myUploadProgress(index),
        }),
      )
        .unwrap()
        .then(() => {
          setUploadingProgress(prev => {
            const newProgress = [...prev];
            newProgress[index] = 100;
            return newProgress;
          });
        })
        .catch(err => {
          setLoading(false);
          setUploadingProgress(prev => {
            const newProgress = [...prev];
            newProgress[index] = 0;
            return newProgress;
          });
          notifyError(err.message);
        });
    }
  };

  const minNumOfVideos = paid ? MIN_NUM_OF_PREMIUM_VIDEOS : MIN_NUM_OF_FREE_VIDEOS;

  const minToUpload = paid
    ? user.numOfPremiumVideos >= minNumOfVideos
      ? 1
      : minNumOfVideos - user.numOfPremiumVideos
    : user.numOfFreeVideos >= minNumOfVideos
    ? 1
    : minNumOfVideos - user.numOfFreeVideos;

  const onVideoDelete = (index, fileName) => {
    setVideos(prev => prev.filter((_, ind) => index !== ind));
    setUploadErrors(prev => prev.filter(error => error.fileName !== fileName));
  };

  const onDeleteMedia = mediaId => {
    openModal(
      'Delete Media',
      `Are you sure? This action will permanently delete the media.`,
      'Delete',
      () => {
        dispatch(deleteInfluencerRequestAlbumMedia({ mediaId }))
          .unwrap()
          .then(() => {
            closeModal();
            dispatch(
              updateNumOfUpdatedMedia({
                numOfMedia: -1,
                type: UPLOAD_FILE_CONSTRAINTS.TYPE.VIDEO,
                paid,
              }),
            );
            notifyInfo('Media deleted successfully!');
          })
          .catch(err => {
            notifyError(err.message);
          });
      },
      'Cancel',
      () => {
        closeModal();
      },
    );
  };

  return (
    <Container>
      <HeaderContainer>
        <BackIconWrapper onClick={onBack}>
          <BackIcon />
        </BackIconWrapper>
        <Text20Bold>Gallery</Text20Bold>
      </HeaderContainer>
      <Header>
        <HeaderRow>
          <Text18Bold>{paid ? 'Paid' : 'Free'} Videos</Text18Bold>
        </HeaderRow>
        <HeaderText>Upload at least {minNumOfVideos} exclusive videos. </HeaderText>
      </Header>
      <Content>
        {pending ? (
          Array.from({ length: 6 }).map((_, index) => <SkeletonContainer key={index} />)
        ) : (
          <>
            {data.map(video => {
              return (
                <MediaItem
                  key={video.id}
                  type={ALBUM_MEDIA_TYPE.PHOTO}
                  kind={video.kind}
                  file={{ url: video.thumbnailUrl }}
                  onDelete={e => {
                    onDeleteMedia(video.id);
                    e.stopPropagation();
                  }}
                />
              );
            })}
            {!!data.length ? <Line /> : <EmptyDiv />}
          </>
        )}
      </Content>
      <Content>
        {videos.map((video, index) => {
          return (
            <MediaItem
              key={index}
              type={ALBUM_MEDIA_TYPE.VIDEO}
              file={video}
              progress={uploadingProgress[index]}
              onDelete={() => onVideoDelete(index, video.file.name)}
              uploadError={uploadErrors.find(error => error.fileName === video.file.name)}
            />
          );
        })}
      </Content>
      <UploadMediaButton
        disabled={loading}
        type={UPLOAD_FILE_CONSTRAINTS.TYPE.VIDEO}
        onChange={files => setVideos([...videos, ...files])}
        setError={setUploadErrors}
        multiple={false}
      />
      <StyledButton
        title={`Save ${minToUpload > videos.length ? `(at least ${minToUpload - videos.length})` : ''}`}
        isLoading={loading}
        onClick={onUploadClick}
        disabled={minToUpload > videos.length || uploadErrors.length}
      />
      <UnsavedChangesModal
        show={showModal}
        setShow={setShowModal}
        onDiscard={() => navigate(`/${UPLOAD_MEDIA}`)}
        onSave={() => {
          onUploadClick();
          setShowModal(false);
        }}
      />
      <PreviewAlbumMediaModal
        modalRef={previewAlbumMediaModalRef}
        type={selectedMedia?.kind}
        url={selectedMedia?.url}
        setSelectedUrl={setSelectedMedia}
      />
    </Container>
  );
};

export default UploadAssistedVideos;
